// Der Befehls-Client: EIN Weg vom Aufruf zur Nutzersprache fuer alle
// Pflegebildschirme.
//
// REINES MODELL, kein React, kein DOM -- ohne Browser pruefbar. Die Fachmodule
// (identifiers.ts, contact-points.ts, addresses.ts) behalten ihre Befehle und
// ihre Saetze; sie beschreiben hier nur noch, WELCHER Satz zu welchem Grund
// gehoert. Wie eine Antwort gelesen wird, steht einmal hier.
//
// DIE ZUSAGEN, die jeder Bildschirm damit erbt:
//   * Jeder Ausgang, der kein Erfolg ist, traegt einen PlainMessage -- Problem,
//     Wirkung, naechster Schritt (NFR-UX-005). Einen Rohcode sieht niemand.
//   * 404 ist neutral: "gibt es nicht" und "darfst du nicht sehen" enden im
//     selben Satz, weil der Server beides gleich beantwortet.
//   * Eine Feldablehnung, die NUR die Absendekennung betrifft, wird zum
//     allgemeinen Rueckfall -- die Nutzerin kann an ihr nichts korrigieren.
//   * Den Sperrstand (lockVersion) erfindet der Client nicht: er schickt, was
//     der Aufrufer aus der geladenen Zeile mitgibt.

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';
import { onlyRequestIdRejected } from './client-request.ts';

export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface FieldError {
  readonly fieldPath: string;
  readonly reasonCode: string;
}

export type CommandOutcome<Applied> =
  | ({ readonly outcome: 'applied' } & Applied)
  | { readonly outcome: 'invalid'; readonly errors: readonly FieldError[] }
  // `reasonCode`: der Grund des Servers, wo er einen nannte -- der Kern liest
  // daran ab, was ausser dem Satz zu tun ist (etwa die Eingabe leeren).
  | { readonly outcome: 'conflict'; readonly message: PlainMessage; readonly reasonCode?: string }
  | { readonly outcome: 'not-found'; readonly message: PlainMessage }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

/**
 * Die Saetze eines Fachmoduls.
 *
 * `conflicts` ordnet die Gruende eines 409 zu; was dort fehlt, bekommt
 * `conflictFallback` -- den Satz "jemand war schneller". `unprocessable` ordnet
 * die Gruende eines 422 OHNE Feldliste zu, die fachlich ein Konflikt sind
 * (etwa ein Endgrund, den es nicht mehr gibt): die Eingabe ist richtig, der
 * Stand hat sich geaendert.
 */
export interface CommandMessages {
  readonly notFound: PlainMessage;
  readonly conflictFallback: PlainMessage;
  readonly conflicts?: Readonly<Record<string, PlainMessage>>;
  readonly unprocessable?: Readonly<Record<string, PlainMessage>>;
  /**
   * Satz fuer 429 mit der Wartezeit aus `retry-after` (Sekunden; fehlt sie,
   * `undefined`). Ohne Eintrag gilt der allgemeine Satz.
   */
  readonly tooManyRequests?: (retryAfterSeconds: number | undefined) => PlainMessage;
  /**
   * Satz fuer 403 bei einem Befehl. Ohne Eintrag gilt der allgemeine Satz zur
   * fehlenden Berechtigung, der von einer Ansicht spricht -- fuer eine Aktion
   * traefe er nicht.
   */
  readonly forbidden?: PlainMessage;
}

export function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return {
    status: response.status,
    answered: payload !== undefined,
    ...correlationOf(body),
  };
}

/**
 * Der Satz zu einem Grund -- nur aus EIGENEN Eintraegen der Tabelle. Ein
 * Grund wie "toString" faende sonst eine geerbte Funktion statt eines Satzes.
 */
function messageFor(
  table: Readonly<Record<string, PlainMessage>> | undefined,
  reasonCode: string | undefined,
): PlainMessage | undefined {
  return table !== undefined && reasonCode !== undefined && Object.hasOwn(table, reasonCode) ? table[reasonCode] : undefined;
}

function reasonCodeOf(record: Record<string, unknown>): string | undefined {
  return typeof record.reasonCode === 'string' ? record.reasonCode : undefined;
}

function fieldErrorsOf(value: unknown[]): readonly FieldError[] {
  return value.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    return typeof row.fieldPath === 'string' && typeof row.reasonCode === 'string'
      ? [{ fieldPath: row.fieldPath, reasonCode: row.reasonCode }]
      : [];
  });
}

function conflictOf(message: PlainMessage, reasonCode: string | undefined): Extract<CommandOutcome<object>, { outcome: 'conflict' }> {
  return reasonCode === undefined ? { outcome: 'conflict', message } : { outcome: 'conflict', message, reasonCode };
}

/**
 * Uebersetzt die Antwort eines Befehls. `applied` liest die fachlichen Felder
 * der Erfolgsantwort -- das Einzige, was je Modul verschieden ist.
 */
export function commandOutcomeOf<Applied>(
  response: Response,
  payload: unknown,
  messages: CommandMessages,
  applied: (record: Record<string, unknown>) => Applied,
): CommandOutcome<Applied> {
  const record = (payload ?? {}) as Record<string, unknown>;
  const reasonCode = reasonCodeOf(record);

  if (response.ok) {
    return { outcome: 'applied', ...applied(record) };
  }
  if (response.status === 404) {
    return { outcome: 'not-found', message: messages.notFound };
  }
  if (response.status === 409) {
    // Je Grund ein eigener Satz: jeder verlangt einen anderen naechsten
    // Schritt, und ein gemeinsamer liesse die Nutzerin raten (NFR-UX-005).
    const message = messageFor(messages.conflicts, reasonCode) ?? messages.conflictFallback;
    return conflictOf(message, reasonCode);
  }
  if (response.status === 422) {
    const unprocessable = messageFor(messages.unprocessable, reasonCode);
    if (unprocessable !== undefined) {
      return conflictOf(unprocessable, reasonCode);
    }
    if (Array.isArray(record.errors)) {
      const errors = fieldErrorsOf(record.errors);
      if (onlyRequestIdRejected(errors)) {
        return { outcome: 'rejected', message: plainMessageFor(undefined, factsOf(response, payload)) };
      }
      return { outcome: 'invalid', errors };
    }
  }
  if (response.status === 403 && messages.forbidden !== undefined) {
    return { outcome: 'rejected', message: messages.forbidden };
  }
  if (response.status === 429 && messages.tooManyRequests !== undefined) {
    const seconds = Number.parseInt(response.headers.get('retry-after') ?? '', 10);
    return { outcome: 'rejected', message: messages.tooManyRequests(Number.isSafeInteger(seconds) && seconds > 0 ? seconds : undefined) };
  }
  return { outcome: 'rejected', message: plainMessageFor(reasonCode, factsOf(response, payload)) };
}

export interface CommandRequest<Applied> {
  readonly url: string;
  readonly accessToken: string;
  readonly body: Record<string, unknown>;
  readonly messages: CommandMessages;
  readonly applied: (record: Record<string, unknown>) => Applied;
  readonly send?: SendLike;
}

/** Schickt einen Befehl als POST mit JSON-Rumpf und uebersetzt die Antwort. */
export async function sendCommand<Applied>(request: CommandRequest<Applied>): Promise<CommandOutcome<Applied>> {
  const send = request.send ?? fetch;
  const response = await send(request.url, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${request.accessToken}` },
    body: JSON.stringify(request.body),
  });
  const payload: unknown = await response.json().catch(() => undefined);
  return commandOutcomeOf(response, payload, request.messages, request.applied);
}

export type ReadOutcome =
  | { readonly outcome: 'loaded'; readonly body: Record<string, unknown> }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

/**
 * Liest einen Abschnitt. 404 traegt KEINEN Satz: welcher Kontakt fehlt, sagt
 * der Bildschirm, und er sagt es neutral.
 */
export async function readResource(options: {
  readonly url: string;
  readonly accessToken: string;
  readonly send?: SendLike;
}): Promise<ReadOutcome> {
  const send = options.send ?? fetch;
  const response = await send(options.url, { headers: { authorization: `Bearer ${options.accessToken}` } });
  const payload: unknown = await response.json().catch(() => undefined);
  if (response.status === 404) {
    return { outcome: 'not-found' };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(body), factsOf(response, payload)) };
  }
  return { outcome: 'loaded', body };
}
